import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router'
import { Dropdown, Modal } from 'react-bootstrap'
import Select from 'react-select'

import DeleteModal from 'pages/parts/DeleteModal'
import FilterSearch from 'pages/parts/FilterSearch'
import CustomTooltip from 'pages/parts/CustomTooltip'
import { GET, POST, PUT, DELETE, httpOk } from 'helpers/api'
import { findKeyFromArrayByValue, formatInventoryQty, notify, returnSign } from 'helpers/helpers'

// Минимальные остатки: пороги по позициям + время суточной сводки.
//
// Кому и по каким каналам приходит сводка, здесь НЕ настраивается — это центр уведомлений
// (/notification-settings, категория «Склад»). Здесь только «что сторожим» и «когда напомнить».

// Время сводки выбирается часами: крон ходит раз в час, минуты в слоте всё равно
// ничего не решают, а выбор из 24 значений короче ввода времени руками.
const HOURS = Array.from({ length: 24 }, (item, index) => String(index).padStart(2, '0') + ':00')

// Выпадашки в модалке рисуем в body: внутри модалки список ложится на таблицу, и сквозь
// него просвечивают карандаши редактируемых ячеек — у них своя точка отсчёта z-index.
const SELECT_PORTAL_PROPS = {
	menuPosition: 'fixed',
	menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
	menuPlacement: 'auto',
	styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
}

function Index() {
	const { t } = useTranslation()

	const reduxAccount = useSelector(state => state.account)

	const [data, setData] = useState([])
	const [counts, setCounts] = useState({ 'all': 0, 'below': 0, 'negative': 0, 'ok': 0 })
	const [poses, setPoses] = useState([])
	const [wmsList, setWmsList] = useState([])
	const [categories, setCategories] = useState([])
	const [productTypes, setProductTypes] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)

	const [filterData, setFilterData] = useState({
		'pos_id': reduxAccount.pos_id,
		'wms_id': null,
		'category_ids': [],
		'search': '',
		'status': 'all',
	})

	// Расписание сводки. Времён может быть несколько: кто-то закупается утром,
	// кто-то смотрит после вечерней инвентаризации, кто-то дважды в день.
	const [settings, setSettings] = useState({ 'is_enabled': true, 'digest_times': [], 'send_when_empty': false })
	const [testing, setTesting] = useState(false)

	// Правка порога прямо в строке: id -> введённое значение (пока не сохранено)
	const [editing, setEditing] = useState({})

	const [modalDelete, setModalDelete] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ 'id': 0 })

	// Модалка добавления
	const [showModal, setShowModal] = useState(false)
	const [modalWmsId, setModalWmsId] = useState(null)
	const [modalSearch, setModalSearch] = useState('')
	const [modalCategories, setModalCategories] = useState([])
	const [modalProducts, setModalProducts] = useState([])
	const modalSearchTimer = useRef(null)
	const [selected, setSelected] = useState({})

	const statusChips = [
		{ 'key': 'all', 'label': t('all'), 'className': 'btn-outline-primary' },
		{ 'key': 'below', 'label': t('stock_alert_below'), 'className': 'btn-outline-danger' },
		{ 'key': 'negative', 'label': t('stock_alert_negative'), 'className': 'btn-outline-warning' },
		{ 'key': 'ok', 'label': t('stock_alert_ok'), 'className': 'btn-outline-success' },
	]

	function buildUrl(customFilterData = filterData) {
		var urlParams = ''

		if (customFilterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + customFilterData.pos_id
		if (customFilterData.wms_id)
			urlParams += returnSign(urlParams) + 'wms_id=' + customFilterData.wms_id
		if (customFilterData.category_ids?.length > 0)
			urlParams += returnSign(urlParams) + 'category_ids=' + customFilterData.category_ids.map(i => i.id).join(',')
		if (customFilterData.search)
			urlParams += returnSign(urlParams) + 'search=' + customFilterData.search
		if (customFilterData.status && customFilterData.status !== 'all')
			urlParams += returnSign(urlParams) + 'status=' + customFilterData.status

		return '/stock-alerts' + urlParams
	}

	async function searchWithFilter(customFilterData = filterData) {
		const response = await GET(buildUrl(customFilterData), {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data.data)
			setCounts(response.data.counts)
			setEditing({})
		}

		setDropdownIsOpen(false)
	}

	function applyStatus(status) {
		const nextFilterData = { ...filterData, 'status': status }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function applySearch(search) {
		const nextFilterData = { ...filterData, 'search': search }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	// --- Порог в строке ---

	async function saveMinQuantity(item) {
		const value = editing[item.id]
		if (value === undefined || value === '' || Number(value) === Number(item.min_quantity)) {
			setEditing({ ...editing, [item.id]: undefined })
			return
		}

		const response = await PUT(`/stock-alerts/${item.id}`, { 'min_quantity': value, 'pos_id': filterData.pos_id })
		if (httpOk(response)) {
			searchWithFilter()
		}
	}

	async function deleteItem() {
		const response = await DELETE(`/stock-alerts/${deleteModalParams.id}`, { 'pos_id': filterData.pos_id }, { loader: true })
		if (httpOk(response)) {
			searchWithFilter()
			setModalDelete(false)
		}
	}

	// --- Расписание ---

	async function getSettings(posId = filterData.pos_id) {
		const response = await GET('/stock-alerts/settings' + (posId ? '?pos_id=' + posId : ''))
		if (httpOk(response)) {
			setSettings(response.data)
		}
	}

	// Час добавляется сразу при выборе в списке, отдельной кнопки «Добавить» нет:
	// с ней выбранное время оставалось только в селекте, «Сохранить» отправляло
	// прежний список, и человек видел, что время «не сохранилось».
	function addTime(time) {
		if (!time) return
		if (settings.digest_times.includes(time)) return

		const times = [...settings.digest_times, time].sort()
		setSettings({ ...settings, 'digest_times': times })
	}

	function removeTime(time) {
		setSettings({ ...settings, 'digest_times': settings.digest_times.filter(item => item !== time) })
	}

	async function saveSettings() {
		// Пустой список сервер молча заменяет на дефолтные 09:00 — для человека это
		// выглядит как «стёр время, а оно вернулось». Лучше сказать прямо.
		if (settings.digest_times.length === 0) {
			notify(t('stock_alert_times_required'), 'error')
			return
		}

		const response = await POST('/stock-alerts/settings', {
			'pos_id': filterData.pos_id,
			'is_enabled': settings.is_enabled,
			'digest_times': settings.digest_times,
			'send_when_empty': settings.send_when_empty,
		}, { loader: true, notification: true })

		if (httpOk(response)) {
			setSettings({ ...settings, 'digest_times': response.data.digest_times })
		}
	}

	// Отправляет сводку прямо сейчас: иначе непонятно, дойдёт вообще или нет.
	// Бэкенд считает сводку и отправку в Telegram-группу отдельно (tg_group в ответе) —
	// сам факт «сводка посчиталась» ещё не значит, что сообщение реально ушло в группу.
	async function sendTest() {
		setTesting(true)
		const response = await POST('/stock-alerts/test', { 'pos_id': filterData.pos_id }, { loader: true })
		setTesting(false)

		if (httpOk(response)) {
			const { items, tg_group, tg_error } = response.data

			if (tg_group === 'sent' || tg_group === null) {
				notify(t('stock_alert_test_sent', { 'items': items }), 'success')
			} else if (tg_group === 'no_endpoint') {
				notify(t('stock_alert_test_tg_no_group'), 'error')
			} else if (tg_group === 'disabled') {
				notify(t('stock_alert_test_tg_disabled'), 'error')
			} else {
				notify(t('stock_alert_test_tg_failed', { 'error': tg_error || '' }), 'error')
			}
		}
	}

	// --- Добавление позиций ---

	// Поиск в модалке дебаунсим: инпут стоит в заголовке колонки, человек печатает
	// подряд, и запрос на каждую букву дёргал бы список из-под пальцев.
	function changeModalSearch(search) {
		setModalSearch(search)

		if (modalSearchTimer.current) clearTimeout(modalSearchTimer.current)
		modalSearchTimer.current = setTimeout(() => {
			getModalProducts(search, modalWmsId, modalCategories)
		}, 500)
	}

	function searchModalNow() {
		if (modalSearchTimer.current) clearTimeout(modalSearchTimer.current)
		getModalProducts(modalSearch, modalWmsId, modalCategories)
	}

	async function getModalProducts(search = modalSearch, wmsId = modalWmsId, cats = modalCategories) {
		var urlParams = ''
		if (filterData.pos_id) urlParams += returnSign(urlParams) + 'pos_id=' + filterData.pos_id
		if (wmsId) urlParams += returnSign(urlParams) + 'wms_id=' + wmsId
		if (cats?.length > 0) urlParams += returnSign(urlParams) + 'category_ids=' + cats.map(i => i.id).join(',')
		if (search) urlParams += returnSign(urlParams) + 'search=' + search

		const response = await GET('/stock-alerts/products' + urlParams)
		if (httpOk(response)) {
			setModalProducts(response.data)
		}
	}

	function toggleModal(bool) {
		if (bool) {
			setSelected({})
			setModalSearch('')
			setModalCategories([])
			setModalWmsId(filterData.wms_id)
			getModalProducts('', filterData.wms_id, [])
		}
		setShowModal(bool)
	}

	function toggleProduct(product) {
		const next = { ...selected }
		// Смотрим на наличие ключа, а не на значение: у только что отмеченной позиции минимум
		// пустая строка, и проверка на истинность снимать галку отказывалась — как и у той,
		// где человек ввёл 0.
		if (next[product.id] !== undefined) {
			delete next[product.id]
		} else {
			next[product.id] = ''
		}
		setSelected(next)
	}

	function setProductQuantity(productId, value) {
		setSelected({ ...selected, [productId]: value })
	}

	async function saveSelected(e) {
		e.preventDefault()

		const items = Object.keys(selected).map(id => ({
			'element_id': Number(id),
			'min_quantity': selected[id] === '' ? 0 : selected[id],
		}))

		if (items.length === 0) {
			notify(t('list_empty'))
			return
		}

		const response = await POST('/stock-alerts', {
			'pos_id': filterData.pos_id,
			'wms_id': modalWmsId,
			'items': items,
		}, { loader: true, notification: true })

		if (httpOk(response)) {
			setShowModal(false)
			searchWithFilter()
		}
	}

	// --- Справочники ---

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getWms(posId = filterData.pos_id) {
		const response = await GET('/helper/wms' + (posId ? '?pos_id=' + posId : ''))
		if (httpOk(response)) setWmsList(response.data)
	}

	async function getProductTypes() {
		const response = await GET('/helper/product-types')
		if (httpOk(response)) setProductTypes(response.data)
	}

	async function getCategories() {
		const response = await GET('/helper/categories')
		if (httpOk(response)) setCategories(response.data)
	}

	function changePos(posId) {
		const nextFilterData = { ...filterData, 'pos_id': posId, 'wms_id': null }
		setFilterData(nextFilterData)
		getWms(posId)
		getSettings(posId)
		searchWithFilter(nextFilterData)
	}

	function statusClass(status) {
		if (status === 'negative') return 'text-warning'
		if (status === 'below') return 'text-danger'
		return 'text-success'
	}

	// Статус в таблице — кругляшок, как в товарах (span.bg-border). Подпись уезжает в тултип:
	// колонка нужна узкая, а три цвета читаются быстрее текста в две строки.
	function statusDotClass(status) {
		if (status === 'negative') return 'bg-warning bg-border'
		if (status === 'below') return 'bg-danger bg-border'
		return 'bg-success bg-border'
	}

	function statusLabel(status) {
		if (status === 'negative') return t('stock_alert_negative')
		if (status === 'below') return t('stock_alert_below')
		return t('stock_alert_ok')
	}

	useEffect(() => {
		searchWithFilter()
		getSettings()
		getPoses()
		getWms()
		getCategories()
		getProductTypes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('stock_alerts')} [{counts.all}]
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModal(true)}>
						{t('add')}
					</button>
				</div>
			</div>

			{/* РАСПИСАНИЕ */}
			<div className="card mb-3">
				<div className="card-body">
					<div className="d-flex flex-wrap align-items-center gap-3">
						<div className="form-check form-switch mb-0">
							<input type="checkbox" className="form-check-input" id="stockAlertEnabled"
								checked={!!settings.is_enabled}
								onChange={(e) => setSettings({ ...settings, 'is_enabled': e.target.checked })} />
							<label className="form-check-label" htmlFor="stockAlertEnabled">
								{t('stock_alert_notify')}
							</label>
						</div>

						<div className="d-flex flex-wrap align-items-center gap-2">
							<b>{t('stock_alert_times')}:</b>
							{settings.digest_times.map((time, index) => (
								<span className="badge bg-primary d-flex align-items-center gap-1" key={index}>
									{time}
									<i className="uil-times" style={{ 'cursor': 'pointer' }} onClick={() => removeTime(time)}></i>
								</span>
							))}
							<select className="form-select" style={{ 'width': '160px' }}
								value=""
								onChange={(e) => addTime(e.target.value)}>
								<option value="">{t('stock_alert_add_time')}</option>
								{HOURS.filter(hour => !settings.digest_times.includes(hour)).map(hour => (
									<option key={hour} value={hour}>{hour}</option>
								))}
							</select>
						</div>

						<div className="form-check mb-0">
							<input type="checkbox" className="form-check-input" id="stockAlertSendWhenEmpty"
								checked={!!settings.send_when_empty}
								onChange={(e) => setSettings({ ...settings, 'send_when_empty': e.target.checked })} />
							<label className="form-check-label" htmlFor="stockAlertSendWhenEmpty">
								{t('stock_alert_send_when_empty')}
							</label>
						</div>

						<div className="d-flex gap-2 ms-auto">
							<button className="btn btn-outline-primary" disabled={testing}
								onClick={() => sendTest()}>
								{t('stock_alert_test')}
							</button>
							<button className="btn btn-primary" onClick={() => saveSettings()}>
								{t('save')}
							</button>
						</div>
					</div>

					<div className="mt-2 text-muted">
						{t('stock_alert_channels_hint')}
						{' '}
						<Link to="/notification-settings">{t('notification_settings')}</Link>
					</div>
				</div>
			</div>

			{/* СПИСОК */}
			<div className="card">
				<div className="card-body">
					<div className="d-flex flex-wrap gap-2 mb-3">
						<div>
							<FilterSearch
								value={filterData.search}
								onSearchChange={(search) => applySearch(search)}>
							</FilterSearch>
						</div>
						<div>
							<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('pos')}</label>
											<Select
												options={poses}
												value={poses.find(option => option.id === filterData.pos_id) || ''}
												onChange={(option) => changePos(option.id)}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
										<div className="form-group col-md-6">
											<label>{t('wms')}</label>
											<Select
												isClearable
												options={wmsList}
												value={wmsList.find(option => option.id === filterData.wms_id) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'wms_id': option ? option.id : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="row">
										<div className="form-group col-md-12">
											<label>{t('category')}</label>
											<Select
												isMulti
												isClearable
												options={categories}
												value={filterData.category_ids || []}
												onChange={(options) => setFilterData({ ...filterData, 'category_ids': options || [] })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end gap-2">
												<button className="btn btn-secondary"
													onClick={() => setDropdownIsOpen(false)}>
													{t('cancel')}
												</button>
												<button className="btn btn-primary"
													onClick={() => searchWithFilter()}>
													{t('apply')}
												</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>

						{/* Быстрые фильтры: минусовые и «ниже минимума» сверху одним кликом */}
						<div className="d-flex flex-wrap gap-2 ms-auto">
							{statusChips.map(chip => (
								<button key={chip.key}
									className={'btn ' + (filterData.status === chip.key ? chip.className.replace('outline-', '') : chip.className)}
									onClick={() => applyStatus(chip.key)}>
									{chip.label} [{counts[chip.key] ?? 0}]
								</button>
							))}
						</div>
					</div>

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '25%' }}>{t('product')}</th>
									<th style={{ 'width': '12%' }} className="td-ellipsis">{t('type')}</th>
									<th style={{ 'width': '18%' }}>{t('category')}</th>
									<th style={{ 'width': '15%' }}>{t('wms')}</th>
									<th style={{ 'width': '13%' }} className="text-end">{t('stock_alert_min')}</th>
									<th style={{ 'width': '13%' }} className="text-end">{t('stock_alert_current')}</th>
									<th style={{ 'width': '6%' }} className="text-nowrap">{t('unit')}</th>
									<th style={{ 'width': '5%' }} className="text-center">{t('status')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data.map((item, index) => (
									<tr key={item.id}>
										<td className="text-center">{index + 1}</td>
										<td>{item.product_name}</td>
										<td className="td-ellipsis">
											{findKeyFromArrayByValue(productTypes, 'id', item.product_type_id, 'name')}
										</td>
										<td>{item.category_name}</td>
										<td>{item.wms_name || t('stock_alert_all_wms')}</td>
										{/* Порог правится прямо в ячейке — той же связкой классов, что цена
											продажи в товарах: серый фон и карандаш вместо рамки */}
										<td className="td-input td-input-editable">
											<div className="td-editable">
												<i className="uil uil-pen td-editable-icon"></i>
												<input type="number" step="0.001" min="0"
													className="form-control text-end"
													autoComplete="off"
													title={t('press_enter_to_save')}
													value={editing[item.id] ?? formatInventoryQty(item.min_quantity)}
													onChange={(e) => setEditing({ ...editing, [item.id]: e.target.value })}
													onBlur={() => saveMinQuantity(item)}
													onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }} />
											</div>
										</td>
										<td className={'text-end fw-bold ' + statusClass(item.status)}>
											{formatInventoryQty(item.balance)}
										</td>
										<td className="text-muted">{item.unit_name}</td>
										<td className="text-center">
											<CustomTooltip text={statusLabel(item.status)}>
												<span className={statusDotClass(item.status)}></span>
											</CustomTooltip>
										</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<div className="table-dropdown-item text-danger"
															onClick={() => {
																setDeleteModalParams({ 'id': item.id })
																setModalDelete(true)
															}}>
															<i className="uil-times"></i>
															{t('delete')}
														</div>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* MODAL: добавление позиций */}
			<Modal show={showModal} animation={false} centered size="lg"
				onHide={() => toggleModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('stock_alert_add_products')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={saveSelected} autoComplete="off">
						<div className="row">
							<div className="form-group col-md-6">
								<label>{t('wms')}</label>
								<Select
									{...SELECT_PORTAL_PROPS}
									isClearable
									options={wmsList}
									value={wmsList.find(option => option.id === modalWmsId) || ''}
									onChange={(option) => {
										const wmsId = option ? option.id : null
										setModalWmsId(wmsId)
										getModalProducts(modalSearch, wmsId, modalCategories)
									}}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
							<div className="form-group col-md-6">
								<label>{t('category')}</label>
								<Select
									{...SELECT_PORTAL_PROPS}
									isMulti
									isClearable
									options={categories}
									value={modalCategories}
									onChange={(options) => {
										setModalCategories(options || [])
										getModalProducts(modalSearch, modalWmsId, options || [])
									}}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
						</div>

						<div className="table-responsive" style={{ 'maxHeight': '45vh', 'overflowY': 'auto' }}>
							<table className="table table-bordered table-sticky-head mb-0">
								<thead>
									<tr>
										<th style={{ 'width': '5%' }}></th>
										{/* Поиск встроен в заголовок колонки: отдельное поле над таблицей
											занимало треть строки фильтров, а ищут всё равно по названию */}
										<th style={{ 'width': '37%' }} className="th-search text-nowrap">
											<i className="uil-search th-search-icon"></i>
											<input type="text" className="th-search-input"
												autoComplete="off"
												placeholder={t('search')}
												value={modalSearch}
												onChange={(e) => changeModalSearch(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														e.preventDefault()
														searchModalNow()
													}
												}} />
										</th>
										<th style={{ 'width': '18%' }} className="td-ellipsis text-nowrap">{t('type')}</th>
										<th style={{ 'width': '15%' }} className="text-end text-nowrap">{t('balance')}</th>
										<th style={{ 'width': '7%' }} className="text-nowrap">{t('unit')}</th>
										<th style={{ 'width': '18%' }} className="text-end text-nowrap">{t('stock_alert_min')}</th>
									</tr>
								</thead>
								<tbody>
									{modalProducts.map((product) => (
										<tr key={product.id}>
											<td className="text-center">
												<input type="checkbox" className="form-check-input"
													checked={selected[product.id] !== undefined}
													onChange={() => toggleProduct(product)} />
											</td>
											<td onClick={() => toggleProduct(product)} style={{ 'cursor': 'pointer' }}>
												{product.name}
												{product.category_name &&
													<span className="text-muted"> · {product.category_name}</span>
												}
											</td>
											<td className="td-ellipsis">
												{findKeyFromArrayByValue(productTypes, 'id', product.product_type_id, 'name')}
											</td>
											<td className="text-end">{formatInventoryQty(product.balance)}</td>
											<td className="text-muted">{product.unit_name}</td>
											{/* Минимум правится прямо в ячейке — той же связкой классов,
												что и порог в основной таблице */}
											<td className="td-input td-input-editable">
												<div className="td-editable">
													<i className="uil uil-pen td-editable-icon"></i>
													<input type="number" step="0.001" min="0"
														className="form-control text-end"
														autoComplete="off"
														disabled={selected[product.id] === undefined}
														value={selected[product.id] ?? ''}
														onChange={(e) => setProductQuantity(product.id, e.target.value)} />
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<div className="d-flex gap-2 w-100 mt-3">
							<div className="vertical-center">
								{t('stock_alert_selected')}: <b>{Object.keys(selected).length}</b>
							</div>
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModal(false)}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100">{t('save')}</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL */}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>
		</>
	)
}

export default Index
